import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AnswerId,
  AnswerMap,
  CHINTU_BEHAVIOUR,
  ChintuButton,
  resolvePlacard,
} from "./chintuStoryEngine";
import ChintuStoryCard from "./ChintuStoryCard";
import "./ChintuPlacard.css";

/**
 * Chintu on a rope. Drops in from the top edge, then swings forever around a
 * top-centre pivot — rope, face and placard rotate as ONE group, which is the
 * whole reason it reads as hanging rather than wobbling.
 *
 * Everything shown here comes from chintuConfiguration.ts: the copy variant,
 * the button list (any number, any labels), and the rope length that turns the
 * same component from "mildly curious" into "barely contained".
 *
 * The wrapper is pointer-events: none so the order feed stays scrollable
 * underneath; only the card itself accepts taps.
 */

interface ChintuPlacardProps {
  /** A message id, or NO_SHOW_PLACARD_ID. */
  placardId: string;
  answers: AnswerMap;
  /** Committed bill when this beat fired — fills {amount} in the copy. */
  amount: number;
  onAnswer: (value: AnswerId) => void;
  onClose: () => void;
}

const SWIPE_CLOSE_PX = 90;

const ChintuPlacard: React.FC<ChintuPlacardProps> = ({
  placardId,
  answers,
  amount,
  onAnswer,
  onClose,
}) => {
  const resolved = resolvePlacard(placardId, answers, amount);

  const [reaction, setReaction] = useState<string | null>(null);
  const [storyOpen, setStoryOpen] = useState(false);
  const [dragY, setDragY] = useState(0);
  const dragStart = useRef<number | null>(null);

  const close = useCallback(() => onClose(), [onClose]);

  // Reaction line holds, then the placard bows out on its own.
  useEffect(() => {
    if (!reaction) return;
    const timer = setTimeout(close, CHINTU_BEHAVIOUR.reactionHoldMs);
    return () => clearTimeout(timer);
  }, [reaction, close]);

  if (!resolved) return null;
  const { copy, buttons, motion } = resolved;

  const handleButton = (button: ChintuButton) => {
    // The answer is recorded either way — `opens` only changes what's shown,
    // never what the finale decides.
    onAnswer(button.id);

    if (button.opens === "story") {
      setStoryOpen(true);
      return;
    }
    if (button.reaction) {
      setReaction(button.reaction);
      return;
    }
    close();
  };

  const handleStoryOk = () => {
    if (CHINTU_BEHAVIOUR.storyClosesPlacard) {
      close();
      return;
    }
    setStoryOpen(false);
  };

  /**
   * Tapping outside or swiping away is a non-answer. useChintuStory records
   * IGNORED for it, so config rules can match on silence specifically.
   */
  const handleIgnore = () => {
    if (storyOpen) {
      handleStoryOk();
      return;
    }
    close();
  };

  return (
    <div className="chintu-scrim">
      {/* Rope, badge and placard all step aside while the ID card is up — the
          card is its own moment, and a swinging rope above a 150-word read is
          just noise. */}
      {!storyOpen && (
      <div
        className="chintu-overlay"
        style={
          {
            "--chintu-rope": `${motion.ropeLen}px`,
            "--chintu-swing": `${motion.swingMs}ms`,
            "--chintu-deg": motion.deg,
            transform: dragY ? `translateY(${dragY}px)` : undefined,
          } as React.CSSProperties
        }
        onPointerDown={(e) => {
          dragStart.current = e.clientY;
        }}
        onPointerMove={(e) => {
          if (dragStart.current === null) return;
          setDragY(Math.max(0, e.clientY - dragStart.current));
        }}
        onPointerUp={() => {
          if (dragY > SWIPE_CLOSE_PX) handleIgnore();
          else setDragY(0);
          dragStart.current = null;
        }}
        onPointerCancel={() => {
          setDragY(0);
          dragStart.current = null;
        }}
      >
        <div className="chintu-swinger">
          <div className="chintu-rope" />
          {/* NineFinds' mark, not Chintu's face — these placards are the app
              talking about him, so he shouldn't appear to be narrating. His
              portrait shows up on the ID card, where he's the subject. */}
          <div className="chintu-rope-badge">
            <img src={CHINTU_BEHAVIOUR.ropeBadgeSrc} alt="" draggable={false} />
          </div>

          <div className="chintu-connector" />
          <div className="chintu-placard" onClick={(e) => e.stopPropagation()}>
            <span className="chintu-placard__kicker">{copy.kicker}</span>
            <h3 className="chintu-placard__headline">{copy.headline}</h3>
            <p className="chintu-placard__sub">{copy.sub}</p>

            {reaction ? (
              <p className="chintu-placard__reaction">
                <em>{reaction}</em>
              </p>
            ) : (
              buttons.length > 0 && (
                <div className="chintu-placard__row">
                  {buttons.map((button) => (
                    <button
                      key={button.id}
                      type="button"
                      className={`chintu-btn chintu-btn--${button.style ?? "ghost"}`}
                      onClick={() => handleButton(button)}
                    >
                      {button.label}
                    </button>
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      </div>
      )}

      {storyOpen && (
        <>
          {/* The placard state is deliberately click-through so the order feed
              keeps scrolling underneath. The card is the opposite: it takes
              pointer events, which both gives us the blur and makes
              tap-outside-to-close actually work. */}
          <div className="chintu-story-backdrop" onClick={handleIgnore} />
          <div className="chintu-story-holder">
            <ChintuStoryCard onOk={handleStoryOk} />
          </div>
        </>
      )}
    </div>
  );
};

export default ChintuPlacard;
